// src/modals/FeedbackModal.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 5429-5611 pre-Plan-37-06).
//
// Scope: UI-15, FB-01 — server-proxied feedback form. POSTs to feedback.php
// (PHP endpoint that forwards to GitHub Issues with the customer-raised label).
// Optional email + screenshot (PNG/JPEG, ≤5 MB) attachments. The screenshot is
// base64-encoded and inlined into the JSON payload — no multipart upload.

import { useState, useRef } from 'react';
import { MessageSquare, Send, Paperclip, X } from 'lucide-react';
import { ClearableInput } from '../App.jsx';   // tiny atom stays in App.jsx (D-01)

export default function FeedbackModal({view,month,onClose}) {
  const [category,setCategory]=useState("suggestion");
  const [description,setDescription]=useState("");
  const [status,setStatus]=useState("idle"); // idle | sending | sent | error
  // FB-01: optional email + screenshot fields
  const [email,setEmail]=useState("");
  const [emailError,setEmailError]=useState("");
  const [screenshot,setScreenshot]=useState(null); // {filename, mime, dataBase64, previewUrl, sizeKb} | null
  const [fileError,setFileError]=useState("");
  const fileInputRef=useRef(null);

  const MAX_SCREENSHOT_BYTES=5*1024*1024; // 5 MB
  const ALLOWED_MIME=["image/png","image/jpeg"];
  const EMAIL_RE=/^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const CATEGORIES=[
    {value:"bug",        label:"Bug report"},
    {value:"feature",    label:"Feature request"},
    {value:"suggestion", label:"Suggestion"},
    {value:"other",      label:"Other"},
  ];

  // Validate email on blur — only if non-empty
  const handleEmailBlur=()=>{
    if(email && !EMAIL_RE.test(email)) setEmailError("Please enter a valid email address");
  };

  // Handle file selection for screenshot attachment
  const handleFileChange=e=>{
    setFileError("");
    const file=e.target.files?.[0];
    if(!file) return;
    if(file.size>MAX_SCREENSHOT_BYTES){
      setFileError("Screenshot must be 5 MB or smaller");
      e.target.value="";
      return;
    }
    if(!ALLOWED_MIME.includes(file.type)){
      setFileError("Only PNG or JPEG screenshots are supported");
      e.target.value="";
      return;
    }
    const reader=new FileReader();
    reader.onload=ev=>{
      // Split data URI prefix to get raw base64 payload
      const dataUrl=ev.target.result;
      const base64=dataUrl.split(",")[1];
      setScreenshot({
        filename:file.name,
        mime:file.type,
        dataBase64:base64,
        previewUrl:dataUrl,
        sizeKb:Math.round(file.size/1024),
      });
    };
    reader.readAsDataURL(file);
  };

  // Remove screenshot and reset file input
  const removeScreenshot=()=>{
    setScreenshot(null);
    setFileError("");
    if(fileInputRef.current) fileInputRef.current.value="";
  };

  // Determine whether send should be disabled
  const sendDisabled=status==="sending"||!description.trim()||(email&&!EMAIL_RE.test(email));

  const send=async()=>{
    if(!description.trim()) return;
    setStatus("sending");
    try {
      // Build payload — omit optional fields when blank (backwards-compatible)
      const payload={category,description:description.trim(),view,month};
      if(email.trim()) payload.email=email.trim();
      if(screenshot) payload.screenshot={filename:screenshot.filename,mime:screenshot.mime,dataBase64:screenshot.dataBase64};
      const res=await fetch("feedback.php",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify(payload),
      });
      if(!res.ok) throw new Error("Server error");
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  };

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={status==="sent"?onClose:undefined}>
      <div style={{background:"var(--c-bg)",borderRadius:18,padding:28,maxWidth:420,width:"100%",boxShadow:"0 8px 40px rgba(0,0,0,0.2)",maxHeight:"90vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:18}}>
          <MessageSquare size={20} className="t-heading-shrink"/>
          <h2 style={{fontFamily:"var(--font-family-display)",fontSize:"var(--font-h2)",color:"var(--c-heading)",margin:0,fontWeight:700}}>Send feedback</h2>
        </div>

        {status==="sent"?(
          <div style={{textAlign:"center",padding:"20px 0"}}>
            <p style={{fontSize:'var(--font-body)',color:"var(--c-text)",marginBottom:18}}>Thanks — your feedback has been logged.</p>
            <button onClick={onClose} style={{background:"var(--c-heading)",color:"#fff",border:"none",borderRadius:20,padding:"9px 28px",fontSize:'var(--font-body)',fontWeight:600}}>Close</button>
          </div>
        ):(
          <>
            <div className="mb-14">
              <label className="label-block">Category</label>
              <select value={category} onChange={e=>setCategory(e.target.value)} style={{width:"100%",padding:"9px 12px",borderRadius:10,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",fontSize:'var(--font-input)'}}> {/* UAT2-GAP-07 iOS zoom floor */}
                {CATEGORIES.map(c=><option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="mb-14">
              <label className="label-block">Description</label>
              <textarea value={description} onChange={e=>setDescription(e.target.value)} rows={4} placeholder="Describe the issue or idea..." style={{width:"100%",padding:"10px 12px",borderRadius:10,border:"1px solid var(--c-border)",background:"var(--c-surface)",color:"var(--c-text)",fontSize:'var(--font-input)',resize:"vertical",boxSizing:"border-box",fontFamily:"inherit"}}/> {/* UAT2-GAP-07 iOS zoom floor */}
            </div>
            {/* FB-01: optional email field */}
            <div className="mb-14">
              <label className="label-block">Email (optional)</label>
              <ClearableInput
                type="email"
                value={email}
                onChange={e=>{setEmail(e.target.value);setEmailError("");}}
                onBlur={handleEmailBlur}
                placeholder="your@email.com"
                wrapperStyle={{width:"100%"}}
                style={{width:"100%",padding:"9px 12px",borderRadius:10,border:`1px solid ${emailError?"var(--c-danger)":"var(--c-border)"}`,background:"var(--c-surface)",color:"var(--c-text)",fontSize:'var(--font-input)',boxSizing:"border-box"}} // UAT2-GAP-07 iOS zoom floor
              />
              {emailError&&<p style={{fontSize:'var(--font-tiny)',color:"var(--c-danger)",margin:"4px 0 0"}}>{emailError}</p>}
            </div>
            {/* FB-01: optional screenshot field */}
            <div style={{marginBottom:18}}>
              <label className="label-block">Screenshot (optional)</label>
              {/* Hidden native file input — triggered by the styled button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleFileChange}
              />
              {!screenshot&&(
                <button
                  type="button"
                  onClick={()=>fileInputRef.current?.click()}
                  style={{display:"inline-flex",alignItems:"center",gap:6,background:"var(--c-surface)",color:"var(--c-text-2)",border:"1px solid var(--c-border)",borderRadius:20,padding:"7px 14px",fontSize:'var(--font-small)',fontWeight:600,cursor:"pointer"}}
                >
                  <Paperclip size={14}/>Add screenshot
                </button>
              )}
              {fileError&&<p style={{fontSize:'var(--font-tiny)',color:"var(--c-danger)",margin:"4px 0 0"}}>{fileError}</p>}
              {/* Preview when a screenshot is attached */}
              {screenshot&&(
                <div style={{marginTop:8}}>
                  <img src={screenshot.previewUrl} alt="Screenshot preview" style={{maxWidth:"100%",maxHeight:140,borderRadius:8,border:"1px solid var(--c-border)",display:"block"}}/>
                  <div style={{display:"flex",alignItems:"center",gap:8,marginTop:6}}>
                    <span style={{fontSize:'var(--font-tiny)',color:"var(--c-text-2)",flex:1,wordBreak:"break-all"}}>{screenshot.filename} · {screenshot.sizeKb} KB</span>
                    <button
                      type="button"
                      onClick={removeScreenshot}
                      style={{display:"inline-flex",alignItems:"center",gap:4,background:"none",border:"none",color:"var(--c-text-3)",cursor:"pointer",padding:"2px 4px",borderRadius:6,fontSize:'var(--font-tiny)'}}
                      title="Remove screenshot"
                    >
                      <X size={14}/>Remove
                    </button>
                  </div>
                </div>
              )}
            </div>
            {status==="error"&&<p style={{fontSize:'var(--font-small)',color:"var(--c-danger)",marginBottom:14}}>Something went wrong — please try again.</p>}
            <div style={{display:"flex",gap:10}}>
              <button type="button" onClick={(e)=>{e.preventDefault();e.stopPropagation();onClose();}} style={{flex:1,background:"var(--c-btn-bg)",color:"var(--c-btn-text)",border:"none",borderRadius:20,padding:"9px 0",fontSize:'var(--font-body)',fontWeight:600}}>Cancel</button>
              <button type="button" onClick={send} disabled={sendDisabled} style={{flex:2,display:"flex",alignItems:"center",justifyContent:"center",gap:8,background:!sendDisabled?"var(--c-heading)":"var(--c-border)",color:!sendDisabled?"#fff":"var(--c-text-3)",border:"none",borderRadius:20,padding:"9px 0",fontSize:'var(--font-body)',fontWeight:600,cursor:!sendDisabled?"pointer":"default"}}>
                <Send size={14}/>{status==="sending"?"Sending…":"Send feedback"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
